import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Crown, 
  Check, 
  Loader2,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LeonMascot from '../components/LeonMascot';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'free',
    name: 'Gratuit',
    price: 0,
    description: 'Accès complet avec publicités',
    features: [
      { text: '3 analyses de devis offertes', included: true },
      { text: 'Recettes travaux illimitées', included: true },
      { text: 'Diagnostic photo illimité', included: true },
      { text: 'Publicités vidéo avant chaque analyse', included: true, negative: true },
      { text: 'Bannières publicitaires', included: true, negative: true },
      { text: 'PDF avec publicité', included: true, negative: true },
      { text: 'Propositions d\'artisans partenaires', included: true, negative: true }
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 9.99,
    period: 'mois',
    description: 'Expérience sans interruption',
    popular: true,
    features: [
      { text: 'Analyses de devis illimitées', included: true },
      { text: 'Recettes travaux illimitées', included: true },
      { text: 'Diagnostic photo illimité', included: true },
      { text: 'Aucune publicité vidéo', included: true },
      { text: 'Aucune bannière publicitaire', included: true },
      { text: 'PDF sans publicité', included: true },
      { text: 'Aucun démarchage commercial', included: true },
      { text: 'Traitement prioritaire', included: true },
      { text: 'Sans engagement', included: true }
    ]
  }
];

export default function Subscription() {
  const { user, isPremium, checkAuth, getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/subscription/status/${sessionId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Erreur');
        
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          await checkAuth();
          return;
        }
        
        if (data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        setPaymentStatus('error');
      } finally {
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleSubscribe = async () => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/create-premium`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur');
      }
      
      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-12 text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-white mb-4">
            Bienvenue dans Premium !
          </h1>
          <p className="text-zinc-400 mb-8">
            Votre abonnement est maintenant actif. Profitez de toutes les fonctionnalités sans limite !
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl transition-all"
          >
            Accéder au tableau de bord
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Sécurisez vos projets de rénovation avec les outils d'audit les plus avancés
          </p>
        </motion.div>

        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-gold-500/10 border border-gold-500/30 rounded-xl flex items-center justify-center gap-3"
          >
            <Crown className="w-5 h-5 text-gold-500" />
            <span className="text-gold-500 font-medium">
              Vous êtes abonné Premium
            </span>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative glass-card p-8 ${
                plan.popular ? 'border-gold-500/50' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-gold rounded-full">
                  <span className="text-xs font-bold text-black uppercase tracking-wider">
                    Populaire
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-heading font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-zinc-500 text-sm">{plan.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-heading font-bold text-white">
                    {plan.price}€
                  </span>
                  {plan.period && (
                    <span className="text-zinc-500">/{plan.period}</span>
                  )}
                </div>
                {plan.id === 'premium' && (
                  <p className="text-zinc-500 text-xs mt-2">
                    Sans engagement — résiliable à tout moment.
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-gold-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-zinc-700 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-zinc-300' : 'text-zinc-600'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.id === 'premium' ? (
                isPremium ? (
                  <Button
                    disabled
                    className="w-full py-6 bg-zinc-800 text-zinc-400 cursor-default"
                  >
                    Plan actuel
                  </Button>
                ) : (
                  <div>
                    <Button
                      onClick={handleSubscribe}
                      disabled={loading || !user}
                      className="w-full py-6 bg-gold-500 hover:bg-gold-400 text-black font-bold"
                      data-testid="subscription-premium-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          <Crown className="w-5 h-5 mr-2" />
                          {user ? 'Passer à Premium' : 'Se connecter'}
                        </>
                      )}
                    </Button>
                    <p className="text-zinc-500 text-xs text-center mt-3">
                      Paiement sécurisé — annulation possible à tout moment.
                    </p>
                  </div>
                )
              ) : (
                <Button
                  disabled
                  className="w-full py-6 bg-zinc-800/50 text-zinc-400 cursor-default"
                >
                  {isPremium ? 'Plan précédent' : 'Plan actuel'}
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-zinc-500 text-sm">
            Paiement sécurisé par Stripe • Annulation à tout moment
          </p>
        </motion.div>
      </div>

      <LeonMascot 
        message={isPremium 
          ? "Vous êtes Premium ! Profitez de toutes les fonctionnalités sans limite."
          : "Avec Premium, vous aurez accès à des audits illimités et au comparateur de devis. C'est l'investissement parfait pour vos projets !"
        }
      />
    </div>
  );
}
